import type { SecureFile } from "features/secureUploads/secureUploads";

import { type FileMoves, fileSize } from "./fileModel";

/** The server's limit for one bulk action (bulk_views.MAX_FILES). */
export const MAX_BULK_FILES = 500;
/** A zip is built in the browser's memory: beyond this, download in parts. */
export const MAX_ZIP_BYTES = 1024 * 1024 * 1024;

/** What an action needs of a selected file, kept while its page is not shown. */
export type SelectedFile = Pick<
  SecureFile,
  | "id"
  | "file_name"
  | "size"
  | "can_delete"
  | "can_restore"
  | "can_purge"
  | "restore_to"
  | "is_confidential"
  | "client_name"
  | "project_id"
  | "project_name"
  | "collection_id"
  | "collection_name"
>;

export interface Selection {
  /** In the order picked, across pages. */
  ids: number[];
  byId: Record<number, SelectedFile>;
  /** The search the selection belongs to: an answer for another is dropped. */
  scope: string;
}

export function emptySelection(scope = ""): Selection {
  return { ids: [], byId: {}, scope };
}

const keep = (file: SelectedFile): SelectedFile => ({
  id: file.id,
  file_name: file.file_name,
  size: file.size,
  can_delete: file.can_delete,
  can_restore: file.can_restore,
  can_purge: file.can_purge,
  restore_to: file.restore_to,
  is_confidential: file.is_confidential,
  client_name: file.client_name,
  project_id: file.project_id,
  project_name: file.project_name,
  collection_id: file.collection_id,
  collection_name: file.collection_name,
});

function withFiles(selection: Selection, files: SelectedFile[]): Selection {
  const ids = [...selection.ids];
  const byId = { ...selection.byId };
  for (const file of files) {
    if (!byId[file.id]) ids.push(file.id);
    byId[file.id] = keep(file);
  }
  return { ...selection, ids, byId };
}

export function drop(selection: Selection, ids: number[]): Selection {
  const gone = new Set(ids);
  if (!selection.ids.some((id) => gone.has(id))) return selection;
  const byId = { ...selection.byId };
  ids.forEach((id) => delete byId[id]);
  return {
    ...selection,
    ids: selection.ids.filter((id) => !gone.has(id)),
    byId,
  };
}

export function toggle(
  selection: Selection,
  file: SelectedFile,
  on: boolean,
): Selection {
  return on ? withFiles(selection, [file]) : drop(selection, [file.id]);
}

/** The header box: every file of the page in, or out. */
export function togglePage(
  selection: Selection,
  page: SelectedFile[],
  on: boolean,
): Selection {
  return on
    ? withFiles(selection, page)
    : drop(
        selection,
        page.map((file) => file.id),
      );
}

/** "Select all": every file of the list - unless the search changed meanwhile. */
export function selectAll(
  selection: Selection,
  files: SelectedFile[],
  scope: string,
): Selection {
  if (scope !== selection.scope) return selection;
  return withFiles(selection, files);
}

/** Rows on screen carry fresher flags than the ones kept; they never add rows. */
export function refresh(selection: Selection, rows: SelectedFile[]): Selection {
  const fresh = rows.filter((row) => selection.byId[row.id]);
  if (fresh.length === 0) return selection;
  const byId = { ...selection.byId };
  fresh.forEach((row) => (byId[row.id] = keep(row)));
  return { ...selection, byId };
}

export function selected(selection: Selection): SelectedFile[] {
  return selection.ids.map((id) => selection.byId[id]);
}

/** The header box: checked when the whole page is in, mixed when part of it. */
export function pageCheck(
  selection: Selection,
  page: { id: number }[],
): { checked: boolean; indeterminate: boolean } {
  const inside = page.filter((file) => selection.byId[file.id]).length;
  return {
    checked: page.length > 0 && inside === page.length,
    indeterminate: inside > 0 && inside < page.length,
  };
}

/** "3 files selected", or "All 47 files selected". */
export function selectionLabel(count: number, total: number): string {
  if (count > 1 && count === total) return `All ${count} files selected`;
  return count === 1 ? "1 file selected" : `${count} files selected`;
}

/** The list a selection is taken from. */
export type SelectionPlace =
  | "individual"
  | "project"
  | "collection"
  | "deleted";

export type BulkActionKey =
  | "add_to_project"
  | "add_to_collection"
  | "move_files"
  | "move_to_project"
  | "download"
  | "delete"
  | "restore"
  | "purge";

export interface BulkAction {
  action: BulkActionKey;
  label: string;
  /** Why it is off; null when it can run. */
  disabled: string | null;
  /** It changes files: off in Client View. */
  write: boolean;
  danger?: boolean;
}

/** What the selection bar offers, in its order: the moves of the place, Download
 * and Delete for live files; Restore and Delete Permanently in Deleted Files.
 * Nothing changes inside a closed project, so there it is Download alone. */
export function bulkActions(
  files: SelectedFile[],
  {
    place,
    moves,
    closed = false,
  }: { place: SelectionPlace; moves: FileMoves; closed?: boolean },
): BulkAction[] {
  const tooMany =
    files.length > MAX_BULK_FILES
      ? `Choose at most ${MAX_BULK_FILES} files at a time.`
      : null;
  if (place === "deleted") {
    const stuck = files.find((file) => file.restore_to?.refusal);
    const actions: BulkAction[] = [];
    if (files.every((file) => file.can_restore))
      actions.push({
        action: "restore",
        label: "Restore",
        disabled:
          tooMany ??
          (stuck ? `${stuck.file_name}: ${stuck.restore_to?.refusal}` : null),
        write: true,
      });
    if (files.every((file) => file.can_purge))
      actions.push({
        action: "purge",
        label: "Delete Permanently",
        disabled: tooMany,
        write: true,
        danger: true,
      });
    return actions;
  }
  const total = files.reduce((sum, file) => sum + file.size, 0);
  const tooBig =
    files.length > 1 && total > MAX_ZIP_BYTES
      ? `These files are ${fileSize(total)} together. Download up to 1 GB at a time.`
      : null;
  const others = files.filter((file) => !file.can_delete).length;
  const notTheirs = others
    ? `You can delete only files you added; ${others} of these ${others === 1 ? "was" : "were"} added by someone else.`
    : null;
  const actions: BulkAction[] = [];
  const move = (action: BulkActionKey, label: string) =>
    actions.push({ action, label, disabled: tooMany, write: true });
  if (!closed && moves.within) {
    if (place === "individual") move("add_to_project", "Add to Project");
    if (place === "project") move("add_to_collection", "Add to Collection");
    if (place === "collection")
      move("move_files", files.length === 1 ? "Move File" : "Move Files");
  }
  if (!closed && moves.across && place !== "individual")
    move("move_to_project", "Move to Project");
  actions.push({
    action: "download",
    label: "Download",
    disabled: tooMany ?? tooBig,
    write: false,
  });
  if (!closed)
    actions.push({
      action: "delete",
      label: "Delete",
      disabled: tooMany ?? notTheirs,
      write: true,
      danger: true,
    });
  return actions;
}

/** Where restored files go back, each place once with how many: "Payroll", 2. */
export function restoreGroups(
  files: SelectedFile[],
): { label: string; individual: boolean; count: number }[] {
  const groups = new Map<string, { individual: boolean; count: number }>();
  for (const file of files) {
    const label = file.restore_to?.label ?? "its place";
    const group = groups.get(label) ?? {
      individual: Boolean(file.restore_to?.individual),
      count: 0,
    };
    group.count += 1;
    groups.set(label, group);
  }
  return Array.from(groups, ([label, group]) => ({ label, ...group }));
}

/** Names inside one zip must differ: "report.pdf", "report (2).pdf". */
export function uniqueNames(names: string[]): string[] {
  const taken = new Set<string>();
  return names.map((raw) => {
    const name = raw.replace(/[\\/]/g, "_");
    const dot = name.lastIndexOf(".");
    const stem = dot > 0 ? name.slice(0, dot) : name;
    const ext = dot > 0 ? name.slice(dot) : "";
    let candidate = name;
    for (let n = 2; taken.has(candidate.toLowerCase()); n += 1)
      candidate = `${stem} (${n})${ext}`;
    taken.add(candidate.toLowerCase());
    return candidate;
  });
}

/** "Acme - Individual Files - 2026-10-26.zip": the client, then the place the
 * files were picked in. */
export function zipName(place: string[], today: Date): string {
  const safe =
    place
      .map((part) => part.replace(/[\\/:*?"<>|]/g, "_").trim())
      .filter(Boolean)
      .join(" - ") || "Files";
  const day = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, "0"),
    String(today.getDate()).padStart(2, "0"),
  ].join("-");
  return `${safe} - ${day}.zip`;
}

/** The page to stay on once files left the list: past the last page the API
 * answers 404, and the table would keep the rows that are gone. */
export function pageAfterRemoval(
  page: number,
  limit: number,
  total: number,
  removed: number,
): number {
  const left = Math.max(0, total - removed);
  return Math.max(1, Math.min(page, Math.ceil(left / limit)));
}
